import axios from 'axios';
import { API_BASE_URL } from '../config';
import { 
  BusinessCard, 
  CardAnalysisStatus, 
  CardAnalysisReport,
  CardAnalysisReportWithData,
  CardAnalysisUpdate,
  CardBatchExportFormat,
  CardBatchExportRequest
} from '../types';

const API_URL = `${API_BASE_URL}/card-analysis`;

/**
 * Upload a business card image for analysis
 * @param title Title for the business card
 * @param file The business card image file
 * @returns The created card object
 */
export const uploadCard = async (title: string, file: File): Promise<BusinessCard> => {
  try {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('file', file);
    
    const response = await axios.post<BusinessCard>(`${API_URL}/upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading card:', error);
    throw error;
  }
};

/**
 * Get a specific card by ID
 * @param cardId The ID of the card to retrieve
 * @returns The card object
 */
export const getCard = async (cardId: string): Promise<BusinessCard> => {
  const response = await axios.get<BusinessCard>(`${API_URL}/cards/${cardId}`);
  return response.data;
};

/**
 * Start the analysis process for a card and stream progress updates
 * @param cardId The ID of the card to analyze
 * @param onUpdate Callback function to handle progress updates
 * @returns A function to abort the analysis process
 */
export const analyzeCard = (
  cardId: string, 
  onUpdate: (update: CardAnalysisUpdate) => void
): { abort: () => void } => {
  const eventSource = new EventSource(`${API_URL}/analyze/${cardId}`);
  const controller = new AbortController();
  
  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as CardAnalysisUpdate;
      onUpdate(data);
      
      if (data.status === 'complete') {
        eventSource.close();
      }
    } catch (error) {
      console.error('Error parsing SSE message:', error);
    }
  };
  
  eventSource.onerror = (error) => {
    console.error('SSE connection error:', error);
    eventSource.close();
    onUpdate({
      status: 'error',
      message: 'Connection error occurred during analysis',
    });
  };
  
  return {
    abort: () => {
      eventSource.close();
      controller.abort();
    },
  };
};

/**
 * Get a specific analysis report by ID
 * @param reportId The ID of the report to retrieve
 * @returns The report object with structured data
 */
export const getReport = async (reportId: string): Promise<CardAnalysisReportWithData> => {
  const response = await axios.get<CardAnalysisReportWithData>(`${API_URL}/reports/${reportId}`);
  return response.data;
};

/**
 * Convert markdown content to PDF
 * @param markdownContent The markdown content to convert
 * @returns Blob containing the PDF data
 */
export const convertToPdf = async (markdownContent: string): Promise<Blob> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const pdfBlob = new Blob(['PDF content would be here'], { type: 'application/pdf' });
      resolve(pdfBlob);
    }, 1000);
  });
};

/**
 * Download a report as a markdown file
 * @param reportContent The markdown content of the report
 * @param filename The name of the file to download
 */
export const downloadMarkdown = (reportContent: string, filename: string): void => {
  const blob = new Blob([reportContent], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'report.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Download a report as a PDF file
 * @param reportContent The markdown content of the report
 * @param filename The name of the file to download
 */
export const downloadPdf = async (reportContent: string, filename: string): Promise<void> => {
  try {
    const pdfBlob = await convertToPdf(reportContent);
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'report.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

/**
 * Upload multiple business card images for batch analysis
 * @param titlePrefix Prefix for the titles of the cards
 * @param files Array of business card image files
 * @returns Array of created card objects
 */
export const batchUploadCards = async (titlePrefix: string, files: File[]): Promise<BusinessCard[]> => {
  try {
    const formData = new FormData();
    formData.append('title_prefix', titlePrefix);
    
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await axios.post<BusinessCard[]>(`${API_URL}/batch-upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error batch uploading cards:', error);
    throw error;
  }
};

/**
 * Start the batch analysis process for multiple cards
 * @param cardIds Array of card IDs to analyze
 * @returns Object containing the report IDs
 */
export const batchAnalyzeCards = async (cardIds: string[]): Promise<{ report_ids: string[] }> => {
  try {
    const response = await axios.post<{ report_ids: string[] }>(`${API_URL}/batch-analyze/`, { card_ids: cardIds });
    return response.data;
  } catch (error) {
    console.error('Error batch analyzing cards:', error);
    throw error;
  }
};

/**
 * Start the batch analysis process for multiple cards with polling for progress
 * @param cardIds Array of card IDs to analyze
 * @param onUpdate Callback function to handle progress updates
 * @returns A function to abort the analysis process
 */
export const batchAnalyzeCardsWithPolling = (
  cardIds: string[], 
  onUpdate: (update: CardAnalysisUpdate) => void
): { abort: () => void } => {
  let isRunning = true;
  const controller = new AbortController();
  const signal = controller.signal;
  
  // Start the batch analysis process
  const startBatchAnalysis = async () => {
    try {
      onUpdate({
        status: 'progress',
        message: 'Starting batch analysis...',
        data: {
          progress: 0,
          current_card_index: 0,
          total_cards: cardIds.length,
          overall_progress: 0
        }
      });
      
      const result = await batchAnalyzeCards(cardIds);
      
      if (isRunning) {
        onUpdate({
          status: 'complete',
          message: 'Batch analysis completed successfully',
          data: {
            progress: 100,
            report_ids: result.report_ids,
            overall_progress: 100,
            total_cards: cardIds.length
          }
        });
      }
    } catch (error) {
      if (isRunning) {
        console.error('Error in batch analysis:', error);
        onUpdate({
          status: 'error',
          message: `Error during batch analysis: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      }
    }
  };
  
  // Start the analysis process
  startBatchAnalysis();
  
  return {
    abort: () => {
      isRunning = false;
      controller.abort();
    },
  };
};

/**
 * Export card data in the specified format
 * @param cardIds Array of card IDs to export
 * @param format Export format (CSV, XLSX, or PDF)
 * @returns Blob containing the exported data
 */
export const exportCards = async (cardIds: string[], format: CardBatchExportFormat): Promise<Blob> => {
  try {
    const exportRequest: CardBatchExportRequest = {
      card_ids: cardIds,
      format: format
    };
    
    const response = await axios.post(`${API_URL}/export/`, exportRequest, {
      responseType: 'blob'
    });
    
    return response.data;
  } catch (error) {
    console.error('Error exporting cards:', error);
    throw error;
  }
};

/**
 * Download exported card data
 * @param cardIds Array of card IDs to export
 * @param format Export format (CSV, XLSX, or PDF)
 * @param filename The name of the file to download
 */
export const downloadExport = async (cardIds: string[], format: CardBatchExportFormat, filename?: string): Promise<void> => {
  try {
    const blob = await exportCards(cardIds, format);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `card_export.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(`Error downloading ${format} export:`, error);
    throw error;
  }
};
